import { Ruler, Hexagon, X } from "lucide-react";

export default function MetaPanel({
  dataset, viewMode,
  exaggeration, setExaggeration,
  overlayOpacity, setOverlayOpacity,
  measureMode, setMeasureMode,
  liveCoord,
}) {
  if (!dataset) {
    return (
      <div style={{ color: "var(--vw-text-dim)", fontFamily: "var(--vw-mono)", fontSize: 11, padding: 8 }}>
        Select a dataset to view metadata.
      </div>
    );
  }

  const isGeo = dataset.kind === "geotiff";

  return (
    <>
      <div className="tbg-vw-section" data-testid="viewer-meta-summary">
        <h4 className="tbg-vw-section-title">Dataset</h4>
        <dl className="tbg-vw-meta">
          <dt>Name</dt><dd>{dataset.name}</dd>
          <dt>Kind</dt><dd>{niceKind(dataset.kind, dataset.file_ext)}</dd>
          <dt>Size</dt><dd>{formatBytes(dataset.size_bytes)}</dd>
          {isGeo && dataset.crs && (<><dt>CRS</dt><dd>{dataset.crs}</dd></>)}
          {isGeo && dataset.width && (
            <><dt>Dimensions</dt><dd>{dataset.width} × {dataset.height} px</dd></>
          )}
          {isGeo && dataset.resolution && (
            <><dt>Resolution</dt><dd>{Math.abs(dataset.resolution[0]).toExponential(2)}° / px</dd></>
          )}
          {isGeo && dataset.bounds && (
            <><dt>Bounds</dt><dd style={{ lineHeight: 1.55 }}>
              W {dataset.bounds[0].toFixed(4)}<br />
              S {dataset.bounds[1].toFixed(4)}<br />
              E {dataset.bounds[2].toFixed(4)}<br />
              N {dataset.bounds[3].toFixed(4)}
            </dd></>
          )}
          {isGeo && dataset.elevation_min != null && (
            <><dt>Elev. min</dt><dd>{dataset.elevation_min.toFixed(2)} m</dd></>
          )}
          {isGeo && dataset.elevation_max != null && (
            <><dt>Elev. max</dt><dd>{dataset.elevation_max.toFixed(2)} m</dd></>
          )}
          {isGeo && dataset.elevation_max != null && dataset.elevation_min != null && (
            <><dt>Range</dt><dd>{(dataset.elevation_max - dataset.elevation_min).toFixed(2)} m</dd></>
          )}
        </dl>
      </div>

      {/* 2D-mode controls */}
      {viewMode === "2d" && isGeo && (
        <div className="tbg-vw-section" data-testid="viewer-meta-2d">
          <h4 className="tbg-vw-section-title">Overlay</h4>
          <div className="tbg-vw-slider">
            <span className="lbl">Opacity</span>
            <input
              type="range" min="0" max="1" step="0.01"
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
              data-testid="viewer-overlay-opacity"
            />
            <span className="val">{Math.round(overlayOpacity * 100)}%</span>
          </div>
        </div>
      )}

      {viewMode === "2d" && (
        <div className="tbg-vw-section" data-testid="viewer-meta-measure">
          <h4 className="tbg-vw-section-title">Measure</h4>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="tbg-vw-btn"
              style={measureMode === "distance" ? { borderColor: "var(--vw-teal)", color: "var(--vw-teal)" } : {}}
              onClick={() => setMeasureMode(measureMode === "distance" ? null : "distance")}
              data-testid="viewer-measure-distance"
            >
              <Ruler size={12} /> Distance
            </button>
            <button
              className="tbg-vw-btn"
              style={measureMode === "area" ? { borderColor: "var(--vw-teal)", color: "var(--vw-teal)" } : {}}
              onClick={() => setMeasureMode(measureMode === "area" ? null : "area")}
              data-testid="viewer-measure-area"
            >
              <Hexagon size={12} /> Area
            </button>
            {measureMode && (
              <button className="tbg-vw-btn ghost" onClick={() => setMeasureMode(null)} title="Stop measuring">
                <X size={12} />
              </button>
            )}
          </div>
          <div style={{ marginTop: 8, color: "var(--vw-text-dim)", fontSize: 10, fontFamily: "var(--vw-mono)" }}>
            {measureMode === "distance" && "Click points to measure straight-line distance."}
            {measureMode === "area" && "Click ≥3 points to measure polygon area."}
            {!measureMode && "Pick a tool to begin."}
          </div>
        </div>
      )}

      {/* 3D-mode controls */}
      {viewMode === "3d" && isGeo && (
        <div className="tbg-vw-section" data-testid="viewer-meta-3d">
          <h4 className="tbg-vw-section-title">Terrain</h4>
          <div className="tbg-vw-slider">
            <span className="lbl">Z exagg.</span>
            <input
              type="range" min="0.2" max="6" step="0.1"
              value={exaggeration}
              onChange={(e) => setExaggeration(parseFloat(e.target.value))}
              data-testid="viewer-z-exaggeration"
            />
            <span className="val">{exaggeration.toFixed(1)}×</span>
          </div>
          <div style={{ marginTop: 8, color: "var(--vw-text-dim)", fontSize: 10, fontFamily: "var(--vw-mono)" }}>
            Drag to orbit. Right-click drag to pan. Scroll to zoom.
          </div>
        </div>
      )}

      {/* Live coord readout (2D) */}
      {viewMode === "2d" && liveCoord && (
        <div className="tbg-vw-section" data-testid="viewer-meta-cursor">
          <h4 className="tbg-vw-section-title">Cursor</h4>
          <dl className="tbg-vw-meta">
            <dt>Lon</dt><dd>{liveCoord[0].toFixed(5)}°</dd>
            <dt>Lat</dt><dd>{liveCoord[1].toFixed(5)}°</dd>
          </dl>
        </div>
      )}
    </>
  );
}

function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function niceKind(k, ext) {
  if (k === "geotiff") return "GeoTIFF DEM";
  if (k === "mesh") return "3D Mesh (OBJ)";
  if (k === "pointcloud_or_mesh") return ext === ".ply" ? "PLY (mesh / cloud)" : "3D";
  return k;
}
